<template>
  <UeModal
    ref="csvImportModal"
    name="csv_import_modal"
    width="95%"
    height="auto"
    :maxWidth="1240"
    :adaptive="true"
    :title="modalParams.title || 'Import CSV'"
    scrollable
    @before-open="resetState"
  >
    <div v-if="errors.length > 0" class="alert alert-warning" role="alert">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </div>
    <!-- Step 1: File Selection -->
    <div v-if="currentStep === 1" class="file-upload-step">
      <div class="file-input-container border rounded p-4 text-center">
        <input
          type="file"
          accept=".csv"
          @change="handleFileChange"
          class="d-none"
          ref="fileInput"
        />
        <div class="mb-3">
          <button type="button" class="btn btn-primary" @click="$refs.fileInput.click()">
            Select CSV File
          </button>
        </div>
        <div v-if="csvFile" class="mt-3">Selected file: {{ csvFile.name }}</div>
      </div>
    </div>

    <!-- Step 2: Field Mapping -->
    <div v-if="currentStep === 2">
      <div class="mb-4">
        <h5 class="mb-3">Map Fields to CSV Columns</h5>
        <div class="text-muted mb-3">
          Select the CSV column that corresponds to each field in the system.
        </div>
      </div>
      <UeTable
        :items="systemFields"
        :fields="mainTableFields"
        :hidePaginationControls="true"
        :useDefaultStyle="true"
        :hideOverflow="false"
        :columnWidths="{ systemField: '40%', csvColumn: '60%' }"
        class="mb-5 fs-sm"
      >
        <template #cell(systemField)="{ item }">
          <div class="font-weight-bold">
            {{ item.label }}
            <span v-if="isRequiredField(item)" class="text-danger">*</span>
          </div>
          <div class="text-muted small" v-if="item.displayType">Type: {{ item.displayType }}</div>
        </template>

        <template #cell(csvColumn)="{ item }">
          <UeSelect
            :options="csvColumnOptions"
            v-model="item.selectedColumn"
            labelField="label"
            keyField="key"
            placeholder="Select CSV column..."
            clearable
            overscrollBehavior="auto"
          />
          <div class="text-muted small mt-1" v-if="item.selectedColumn">
            Preview: {{ getPreviewForColumn(item.selectedColumn) }}
          </div>
        </template>
      </UeTable>
      <div v-for="(section, i) in additionalSections" :key="i">
        <h5 class="mb-3">{{ section.title }}</h5>
        <div v-if="section.description" class="text-muted mb-3">{{ section.description }}</div>
        <UeTable
          v-if="additionalFields[i].length > 0"
          :items="getAdditionalFieldsWithIndex(i)"
          :fields="additionalTableFields"
          :hidePaginationControls="true"
          :useDefaultStyle="true"
          :hideOverflow="false"
          :columnWidths="{ systemField: '40%', csvColumn: '50%', action: '10%' }"
          class="mb-3 fs-sm"
        >
          <template #cell(systemField)="{ item }">
            <UeSelect
              :options="additionalSections[i].fields"
              v-model="additionalFields[i][item.index]"
              labelField="label"
              keyField="key"
              placeholder="Select field..."
              overscrollBehavior="auto"
            />
          </template>

          <template #cell(csvColumn)="{ item }">
            <UeSelect
              :options="csvColumnOptions"
              v-if="additionalFields[i][item.index]"
              v-model="additionalFields[i][item.index].selectedColumn"
              labelField="label"
              keyField="key"
              placeholder="Select CSV column..."
              clearable
              overscrollBehavior="auto"
            />
            <div
              class="text-muted small mt-1"
              v-if="additionalFields[i][item.index]?.selectedColumn"
            >
              Preview: {{ getPreviewForColumn(additionalFields[i][item.index]?.selectedColumn) }}
            </div>
          </template>

          <template #cell(action)="{ item }">
            <button class="btn btn-link btn-sm" @click="removeAdditionalField(i, item.index)">
              Delete
            </button>
          </template>
        </UeTable>
        <button
          v-if="additionalSections[i].fields?.length > 0"
          class="btn btn-secondary"
          @click="addAdditionalField(i)"
        >
          Add field
        </button>
        <p v-else>No fields available to add</p>
      </div>
    </div>

    <template #footer>
      <div class="w-100 d-flex justify-content-between align-items-center">
        <div>
          <!-- Don't remove the above wrapper <div> -->
          <button
            v-if="currentStep === 2"
            type="button"
            class="btn btn-link"
            @click="currentStep = 1"
          >
            ← Back to File Selection
          </button>
        </div>
        <div class="d-flex">
          <button type="button" class="btn btn-secondary mr-2" @click="closeModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="currentStep === 2"
            :disabled="!canProceed"
            @click="handleStepAction"
          >
            Import
          </button>
        </div>
      </div>
    </template>
  </UeModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Papa from 'papaparse'

import { useModalStore } from '@/stores/ModalStore'
import UeTable from '@components/base/UeTable.vue'
import {
  Constraint,
  CsvField,
  CsvOption,
  Field,
  ImportPayload,
  MappingPayload,
  ParseResult,
  buildCsvFields,
  buildImportMappingPayload,
} from '@lib/csvImport'

const MODAL_NAME = 'csv_import_modal'

interface AdditionalSection {
  title: string
  description?: string
  fields: Field[]
}

interface ModalParams {
  additionalSections?: AdditionalSection[]
  onSubmit: (payload: ImportPayload) => Promise<void>
  beforeSubmit: (payload: ImportPayload) => ImportPayload
  mapFields: Field[]
  title: string
}

export default defineComponent({
  name: 'ImportModal',
  components: {
    UeTable,
  },
  data() {
    return {
      // Determine which step to show
      currentStep: 1,
      // Set when the CSV file is selected
      selectedCsvFile: null as File | null,
      // Set when the CSV file is parsed
      parsedRows: [] as string[][],
      // Set when the the CSV file is parsed, and headers are extracted
      csvFields: [] as CsvField[],
      // Set when the modal is open and modified on each user selection
      systemFields: [] as Field[],
      // Holds an array of errors
      additionalFields: [] as Field[][],
      errors: [] as string[],
    }
  },
  computed: {
    modalParams(): ModalParams {
      return (useModalStore().getModalParams(MODAL_NAME) || {}) as ModalParams
    },
    mapFields(): Field[] {
      return this.modalParams.mapFields
    },
    additionalSections(): AdditionalSection[] {
      return this.modalParams.additionalSections || []
    },
    csvFile(): File | null {
      // This allows us to pass a file to the modal
      // Useful for setting a state in Storybook.
      return this.modalParams.csvFile || this.selectedCsvFile
    },
    canProceed(): boolean {
      if (this.errors.length > 0) {
        return false
      }

      if (this.currentStep === 1) {
        return !!this.csvFile
      } else {
        return this.validMapping
      }
    },
    validMapping(): boolean {
      return this.systemFields.every(
        (field: Field) => !this.isRequiredField(field) || field.selectedColumn !== null,
      )
    },
    csvColumnOptions(): CsvOption[] {
      return this.csvFields.map((field: CsvField) => ({
        key: field.name.toString(),
        label: field.name,
      }))
    },
    csvFieldsByName(): Record<string, CsvField> {
      return this.csvFields.reduce(
        (acc: Record<string, CsvField>, csvField: CsvField) => {
          acc[csvField.name] = csvField
          return acc
        },
        {} as Record<string, CsvField>,
      )
    },
    mainTableFields() {
      return [
        {
          key: 'systemField',
          label: 'System Field',
        },
        {
          key: 'csvColumn',
          label: 'CSV Column',
        },
      ]
    },
    additionalTableFields() {
      return [
        {
          key: 'systemField',
          label: 'System Field',
        },
        {
          key: 'csvColumn',
          label: 'CSV Column',
        },
        {
          key: 'action',
          label: 'Action',
        },
      ]
    },
  },
  watch: {
    csvFile(newFile) {
      if (newFile) {
        this.parseCsvFile()
      }
    },
    mapFields(fields) {
      if (fields) {
        this.systemFields = fields.map((field: Field) => ({
          ...field,
          selectedColumn: null,
        }))
      }
    },
    additionalSections(sections: AdditionalSection[]) {
      this.additionalFields = Array(sections.length).fill([])
    },
  },
  methods: {
    getAdditionalFieldsWithIndex(sectionIndex: number) {
      return this.additionalFields[sectionIndex].map((field, index) => ({
        ...field,
        index,
      }))
    },
    addAdditionalField(sectionIndex: number): void {
      this.additionalFields[sectionIndex].push(null)
    },
    removeAdditionalField(sectionIndex: number, fieldIndex: number): void {
      this.additionalFields[sectionIndex].splice(fieldIndex, 1)
    },
    clearErrors(): void {
      this.errors = []
    },
    isRequiredField(field: Field): boolean {
      return (
        field.constraints?.some((constraint: Constraint) => constraint.type === 'required') ?? false
      )
    },
    getPreviewForColumn(option: CsvOption): string | undefined {
      return this.csvFieldsByName[option.key]?.preview
    },
    handleFileChange(event: Event): void {
      this.clearErrors()
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]

      if (!file || !file.name.toLowerCase().endsWith('.csv')) {
        return
      }

      this.selectedCsvFile = file
    },
    // Remove selections
    resetSelections(): void {
      this.systemFields.forEach((field: Field) => {
        field.selectedColumn = null
      })
    },
    parseCsvFile(): void {
      if (!this.csvFile) return

      Papa.parse(this.csvFile, {
        complete: (results: ParseResult) => {
          if (results.data.length < 2) {
            this.errors.push('The CSV file appears to be empty')
            return
          }

          this.parsedRows = results.data
          this.csvFields = buildCsvFields(results)
          this.resetSelections()
          this.currentStep = 2
        },
        error: (error: { message: string }) => {
          this.errors.push(`Error reading CSV: ${error.message}`)
        },
      })
    },
    handleStepAction(): void {
      if (this.currentStep === 1 && this.csvFile) {
        this.currentStep = 2
      } else if (this.currentStep === 2) {
        this.submitMapping()
      }
    },
    submitMapping() {
      const mapping = buildImportMappingPayload(this.systemFields, this.csvFieldsByName)
      const additionalMapping = this.additionalFields.reduce(
        (acc: MappingPayload[], fields: Field[]) => {
          return acc.concat(buildImportMappingPayload(fields, this.csvFieldsByName))
        },
        [],
      )

      let payload = {
        rows: this.parsedRows.slice(1),
        mapping,
        additionalMapping,
        csv_file: this.csvFile,
      }

      if (this.modalParams.beforeSubmit) {
        payload = this.modalParams.beforeSubmit(payload)
      } else {
        payload = {
          ...payload,
          rows: JSON.stringify(payload.rows),
          mapping: JSON.stringify(mapping.concat(additionalMapping)),
        }
        delete payload.additionalMapping
      }

      if (this.modalParams.onSubmit) {
        this.modalParams.onSubmit(payload).then(() => {
          this.closeModal()
        })
      }
    },
    resetState(): void {
      this.currentStep = 1
      this.selectedCsvFile = null
      this.csvFields = []
      this.parsedRows = []
      this.errors = []
      this.resetSelections()

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    closeModal(): void {
      this.$closeModal(MODAL_NAME)
    },
  },
})
</script>

<style lang="sass" scoped>
.file-input-container
  min-height: 200px
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
</style>
